import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Phone, Instagram, Download, ArrowUpRight, Sparkles, Check, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1708019931163-a3298faf915d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxibGFjayUyMHNwb3J0cyUyMGNhciUyMGRldGFpbCUyMHN0dWRpb3xlbnwwfHx8fDE3ODI2Nzg3MzJ8MA&ixlib=rb-4.1.0&q=85";
const EXTERIOR_IMG = "https://images.unsplash.com/photo-1633014041037-f5446fb4ce99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxjYXIlMjB3YXNoJTIwZm9hbSUyMGRhcmt8ZW58MHx8fHwxNzgyNjc4NzMyfDA&ixlib=rb-4.1.0&q=85";
const INTERIOR_IMG = "https://images.unsplash.com/photo-1604432264352-54fdecccf881?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjYXIlMjBpbnRlcmlvciUyMGNsZWFuaW5nJTIwc3RlZXJpbmclMjB3aGVlbHxlbnwwfHx8fDE3ODI2Nzg3MzJ8MA&ixlib=rb-4.1.0&q=85";

const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.6 6.3a5.6 5.6 0 0 1-3.6-1.3 5.6 5.6 0 0 1-1.8-3.1V1.5h-3.4v13.6a2.7 2.7 0 1 1-1.9-2.6V9a6.1 6.1 0 1 0 5.3 6V8.4a8.8 8.8 0 0 0 5.4 1.8V6.8c0-.2 0-.4-.1-.5z" />
  </svg>
);

const PKG_IMAGES = {
  "exterior-wash": EXTERIOR_IMG,
  "interior-wash": INTERIOR_IMG,
  "full-detail": HERO_IMG,
};

function Landing() {
  const [info, setInfo] = useState({});
  const [packages, setPackages] = useState([]);
  const [extras, setExtras] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", vehicle: "Sedan",
    package_slug: "full-detail", preferred_date: "", preferred_time: "", notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState({}); // {date: dayObj}
  const [dateOpen, setDateOpen] = useState(false);
  const [lastBooking, setLastBooking] = useState(null); // {cancel_token, ...} after successful submit

  useEffect(() => {
    axios.get(`${api}/business-info`).then(r => setInfo(r.data)).catch(() => {});
    axios.get(`${api}/packages`).then(r => setPackages(r.data)).catch(() => {});
    axios.get(`${api}/extras`).then(r => setExtras(r.data)).catch(() => {});
    axios.get(`${api}/availability/slots?days=60`).then(r => {
      const m = {};
      (r.data.days || []).forEach(d => { m[d.date] = d; });
      setSlotsByDate(m);
    }).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(`${api}/pricing-sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "gi-detailing-pricing.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pricing sheet downloaded.");
    } catch (e) {
      toast.error("Could not download PDF. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Name and phone are required.");
      return;
    }
    if (!form.preferred_date) {
      toast.error("Please pick an available date.");
      return;
    }
    if (!form.preferred_time) {
      toast.error("Please pick an available time.");
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${api}/bookings`, form);
      toast.success("Booking request sent. Check your email for confirmation.");
      setLastBooking(res.data);
      // Refresh slots so the just-taken slot disappears
      const r = await axios.get(`${api}/availability/slots?days=60`);
      const m = {};
      (r.data.days || []).forEach(d => { m[d.date] = d; });
      setSlotsByDate(m);
      setForm({ ...form, full_name: "", phone: "", email: "", preferred_date: "", preferred_time: "", notes: "" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-ink text-white relative">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ink/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <button
            data-testid="nav-brand"
            onClick={() => scrollTo("top")}
            className="flex items-center group"
          >
            <img src="/brand/gi-logo.jpg" alt="GI Detailing" style={{ mixBlendMode: 'lighten' }} className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105" />
          </button>
          <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            <button data-testid="nav-services" onClick={() => scrollTo("services")} className="hover:text-white transition-colors">Services</button>
            <button data-testid="nav-extras" onClick={() => scrollTo("extras")} className="hover:text-white transition-colors">Extras</button>
            <button data-testid="nav-book" onClick={() => scrollTo("book")} className="hover:text-white transition-colors">Book</button>
          </nav>
          <button
            data-testid="nav-download-pdf"
            onClick={downloadPdf}
            disabled={downloading}
            className="hidden sm:flex beam-btn items-center gap-2 border border-white/20 hover:border-white px-4 py-2 text-[11px] font-mono uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? "..." : "Pricing PDF"}
          </button>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative pt-32 md:pt-40 pb-24 md:pb-32 noise-overlay overflow-hidden">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ink/40 via-ink/80 to-ink" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-zinc-400 mb-6 flex items-center justify-center gap-3 reveal">
            <span className="inline-block w-8 h-px bg-zinc-600" />
            EST. DMV · Mobile Detail Studio
            <span className="inline-block w-8 h-px bg-zinc-600" />
          </div>
          <h1 data-testid="hero-title" className="sr-only">GI Detailing</h1>
          <img
            src="/brand/gi-logo.jpg"
            alt="GI Detailing"
            style={{ mixBlendMode: 'lighten' }}
            className="w-[320px] sm:w-[480px] md:w-[640px] h-auto mx-auto block select-none pointer-events-none reveal"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-end mt-12">
          <div className="md:col-span-8 reveal">
            <p className="max-w-xl text-lg md:text-xl text-zinc-300 leading-relaxed font-light">
              {info.tagline || "Precision Automotive Restyling & Care"} — meticulous hand-detailing for vehicles that deserve more than a quick rinse.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                data-testid="hero-cta-book"
                onClick={() => scrollTo("book")}
                className="beam-btn bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-7 py-4 hover:bg-zinc-200 transition-colors flex items-center gap-2"
              >
                Book Appointment <ArrowUpRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-cta-pdf"
                onClick={downloadPdf}
                disabled={downloading}
                className="beam-btn border border-white/20 hover:border-white font-mono text-xs uppercase tracking-[0.25em] px-7 py-4 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {downloading ? "Generating..." : "Download Pricing Sheet"}
              </button>
            </div>
          </div>
          <div className="md:col-span-4 reveal" style={{ animationDelay: "200ms" }}>
            <div className="border border-white/10 bg-surface/60 backdrop-blur-md p-6 space-y-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Contact Channel</div>
              <a href={`tel:${(info.phone || "").replace(/\D/g, "")}`} className="block group" data-testid="contact-phone">
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-1">Phone</div>
                <div className="text-2xl font-serif group-hover:text-white text-zinc-200 transition-colors flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {info.phone || "(301) 201-4263"}
                </div>
              </a>
              <div className="h-px bg-white/10" />
              <div data-testid="contact-location">
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-1">Location</div>
                <div className="text-lg flex items-center gap-2 text-zinc-200">
                  <MapPin className="w-4 h-4" /> {info.location || "DMV Area"}
                </div>
              </div>
              <div className="h-px bg-white/10" />
              <div className="flex gap-3 pt-2">
                <a href={info.instagram_url || "https://www.instagram.com/gi_datailing?igsh=MW94b3ByNnRhb2tuag=="} target="_blank" rel="noreferrer"
                   data-testid="contact-instagram"
                   className="flex-1 border border-white/10 hover:border-white px-3 py-2.5 flex items-center gap-2 transition-colors group">
                  <Instagram className="w-4 h-4" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 group-hover:text-white">{info.instagram || "@gi_detailing"}</span>
                </a>
                <a href={info.tiktok_url || "https://www.tiktok.com/@gi.detailing1?_r=1&_t=ZP-97b5zKNuKzO"} target="_blank" rel="noreferrer"
                   data-testid="contact-tiktok"
                   className="flex-1 border border-white/10 hover:border-white px-3 py-2.5 flex items-center gap-2 transition-colors group">
                  <TikTokIcon className="w-4 h-4" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 group-hover:text-white">{info.tiktok || "@gi.detailing1"}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="mt-24 border-y border-white/10 py-4 overflow-hidden">
          <div className="flex gap-12 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 whitespace-nowrap animate-pulse-slow">
            <span>· Hand Wash</span><span>· Paint Decontamination</span><span>· Cabin Restoration</span>
            <span>· Headlight Restoration</span><span>· Engine Bay Detail</span><span>· Pet Hair Removal</span>
            <span>· Clay Bar + Wax</span><span>· Mobile Service · DMV ·</span>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-zinc-500 mb-3">— Packages 01 / 03</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-2xl">
                Three signature programs.<br/>
                <span className="text-zinc-500 italic">Every inch accounted for.</span>
              </h2>
            </div>
            <div className="text-sm text-zinc-400 max-w-xs font-light">
              Pricing scales by vehicle category. All work performed by hand with premium detailing chemistry.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {packages.map((p, idx) => {
              const isWide = idx === 2;
              return (
                <article
                  key={p.id}
                  data-testid={`package-card-${p.slug}`}
                  className={`group relative border border-white/10 hover:border-white/30 bg-surface transition-all duration-300 hover:-translate-y-1 overflow-hidden ${isWide ? "md:col-span-12" : "md:col-span-6"}`}
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={PKG_IMAGES[p.slug] || HERO_IMG}
                        alt={p.name}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                      <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 bg-ink/70 backdrop-blur px-2 py-1 border border-white/10">
                        0{idx + 1}
                      </div>
                    </div>
                    <div className="p-7 md:p-9 flex flex-col">
                      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
                        <h3 className="font-serif text-3xl md:text-4xl tracking-tight">{p.name}</h3>
                        {p.duration && (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                            {p.duration}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400 mb-6 font-light">{p.description}</p>

                      {/* Pricing matrix */}
                      <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-6">
                        {[
                          { label: "Sedan", price: p.sedan_price },
                          { label: "SUV", price: p.suv_price },
                          { label: "Truck", price: p.truck_price },
                        ].map((row) => (
                          <div key={row.label} className="bg-ink p-4 text-center">
                            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{row.label}</div>
                            <div className="font-serif text-2xl md:text-3xl">${Math.round(row.price)}</div>
                          </div>
                        ))}
                      </div>

                      <ul className="space-y-2 mb-2">
                        {p.features.map((f) => (
                          <li key={`${p.slug}-${f}`} className="flex items-start gap-3 text-sm text-zinc-300">
                            <Check className="w-3.5 h-3.5 mt-1 text-zinc-500 flex-shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        data-testid={`package-book-${p.slug}`}
                        onClick={() => { setForm({ ...form, package_slug: p.slug }); scrollTo("book"); }}
                        className="mt-6 self-start text-xs font-mono uppercase tracking-[0.25em] text-zinc-400 hover:text-white border-b border-white/20 hover:border-white pb-1 transition-colors flex items-center gap-2"
                      >
                        Book this package <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXTRAS */}
      <section id="extras" className="relative py-24 md:py-32 px-6 md:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-zinc-500 mb-3">— Add-ons 02 / 03</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Extras<br/>
              <span className="italic text-zinc-500">à la carte.</span>
            </h2>
            <p className="mt-6 text-zinc-400 font-light text-sm">
              Stack any extra on top of a package — or book them on their own. Final pricing depends on vehicle size & condition.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              <Sparkles className="w-3 h-3" /> Quote on inspection
            </div>
          </div>
          <div className="md:col-span-8 border-t border-white/10">
            {extras.map((e) => (
              <div
                key={e.id}
                data-testid={`extra-row-${e.id}`}
                className="flex items-baseline justify-between gap-6 py-6 border-b border-white/10 group hover:px-4 transition-all"
              >
                <span className="font-serif text-2xl md:text-3xl text-zinc-200 group-hover:text-white">{e.name}</span>
                <span className="flex-1 mx-4 border-b border-dashed border-white/10" />
                <span className="font-mono text-base md:text-lg whitespace-nowrap">
                  ${Math.round(e.price_min)} <span className="text-zinc-600">—</span> ${Math.round(e.price_max)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section id="book" className="relative py-24 md:py-32 px-6 md:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-zinc-500 mb-3">— Booking 03 / 03</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Request your<br/>
              <span className="italic text-zinc-500">appointment.</span>
            </h2>
            <p className="mt-6 text-zinc-400 font-light text-sm max-w-md">
              Tell us about your vehicle and preferred timing — we&apos;ll confirm via phone within hours.
            </p>
            <div className="mt-10 space-y-4 text-sm">
              <a href={`tel:${(info.phone || "").replace(/\D/g, "")}`} className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors" data-testid="book-call-phone">
                <span className="w-9 h-9 border border-white/10 grid place-items-center"><Phone className="w-4 h-4" /></span>
                {info.phone}
              </a>
              <a href={info.instagram_url || "https://www.instagram.com/gi_datailing?igsh=MW94b3ByNnRhb2tuag=="} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors" data-testid="book-instagram">
                <span className="w-9 h-9 border border-white/10 grid place-items-center"><Instagram className="w-4 h-4" /></span>
                {info.instagram}
              </a>
              <a href={info.tiktok_url || "https://www.tiktok.com/@gi.detailing1?_r=1&_t=ZP-97b5zKNuKzO"} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors" data-testid="book-tiktok">
                <span className="w-9 h-9 border border-white/10 grid place-items-center"><TikTokIcon className="w-4 h-4" /></span>
                {info.tiktok}
              </a>
            </div>
          </div>
          <form onSubmit={submitBooking} className="md:col-span-7 border border-white/10 bg-surface/60 p-7 md:p-10 space-y-5" data-testid="booking-form">
            {lastBooking && (
              <div className="border border-emerald-500/30 bg-emerald-500/5 p-5 mb-2" data-testid="booking-success-banner">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">Booking received · pending confirmation</div>
                <p className="text-sm text-zinc-300 mb-3">
                  Thanks, <strong>{lastBooking.full_name}</strong>. We&apos;ll confirm your appointment shortly{lastBooking.email ? " — check your inbox for details" : ""}.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <a
                    href={`/cancel/${lastBooking.cancel_token}`}
                    data-testid="booking-cancel-link"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] border border-white/20 hover:border-white text-zinc-300 hover:text-white px-4 py-2 transition-colors"
                  >
                    Cancel my booking
                  </a>
                  <button
                    type="button"
                    onClick={() => setLastBooking(null)}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-white transition-colors"
                  >
                    Make another booking
                  </button>
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input data-testid="booking-name" required value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} className="input-field" placeholder="Your name" />
              </Field>
              <Field label="Phone" required>
                <input data-testid="booking-phone" required value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="input-field" placeholder="(301) 555-0100" />
              </Field>
            </div>
            <Field label="Email">
              <input data-testid="booking-email" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="input-field" placeholder="you@email.com" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Vehicle Type">
                <select data-testid="booking-vehicle" value={form.vehicle} onChange={(e)=>setForm({...form, vehicle: e.target.value})} className="input-field">
                  <option>Sedan</option><option>SUV</option><option>Truck</option>
                </select>
              </Field>
              <Field label="Package">
                <select data-testid="booking-package" value={form.package_slug} onChange={(e)=>setForm({...form, package_slug: e.target.value})} className="input-field">
                  {packages.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Preferred Date">
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="booking-date"
                    className="input-field text-left flex items-center justify-between"
                  >
                    <span className={form.preferred_date ? "text-white" : "text-zinc-500"}>
                      {form.preferred_date || "Select an available date"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-zinc-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-auto p-0 bg-ink border border-white/20 text-white"
                  data-testid="booking-date-popover"
                >
                  <Calendar
                    mode="single"
                    selected={form.preferred_date ? new Date(form.preferred_date + "T12:00:00") : undefined}
                    onSelect={(d) => {
                      if (!d) return;
                      const iso = isoDate(d);
                      setForm({ ...form, preferred_date: iso, preferred_time: "" });
                      setDateOpen(false);
                    }}
                    disabled={(d) => {
                      const iso = isoDate(d);
                      const today = isoDate(new Date());
                      if (iso < today) return true;
                      const day = slotsByDate[iso];
                      if (!day) return true;
                      if (!day.enabled) return true;
                      return !day.has_capacity;
                    }}
                    initialFocus
                  />
                  <div className="px-3 py-2 border-t border-white/10 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                    Tue · Thu · Sun → 6 AM – 7 PM · cap 4 &nbsp;|&nbsp; Mon · Wed · Fri · Sat → 5 PM – 8 PM · cap 1
                  </div>
                </PopoverContent>
              </Popover>
            </Field>

            {form.preferred_date && (
              <Field label="Preferred Time">
                <TimeSlotGrid
                  day={slotsByDate[form.preferred_date]}
                  selected={form.preferred_time}
                  onChange={(t) => setForm({ ...form, preferred_time: t })}
                />
              </Field>
            )}
            <Field label="Notes">
              <textarea data-testid="booking-notes" rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="input-field resize-none" placeholder="Anything we should know about the vehicle..." />
            </Field>
            <button
              data-testid="booking-submit"
              type="submit"
              disabled={submitting}
              className="beam-btn w-full bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? "Sending..." : <>Request Appointment <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-6">
          <img src="/brand/gi-logo.jpg" alt="GI Detailing" style={{ mixBlendMode: 'lighten' }} className="h-10 w-auto" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            © {new Date().getFullYear()} · Premium Quality Service · DMV Area
          </div>
          <button
            data-testid="footer-download-pdf"
            onClick={downloadPdf}
            disabled={downloading}
            className="beam-btn border border-white/20 hover:border-white font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-3 h-3" />
            {downloading ? "Generating..." : "Download Pricing PDF"}
          </button>
        </div>
      </footer>

      <style>{`
        .input-field {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          padding: 12px 14px;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }
        .input-field:focus { border-color: rgba(255,255,255,0.5); }
        .input-field::placeholder { color: #555; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block">
        {label}{required && <span className="text-white"> *</span>}
      </span>
      {children}
    </label>
  );
}

function TimeSlotGrid({ day, selected, onChange }) {
  if (!day || !day.enabled) {
    return <div className="text-sm text-zinc-500 font-light italic" data-testid="time-slot-empty">Closed.</div>;
  }
  if (!day.slots || day.slots.length === 0) {
    return <div className="text-sm text-zinc-500 font-light italic" data-testid="time-slot-empty">No slots.</div>;
  }
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="time-slot-grid">
      {day.slots.map(s => {
        const active = selected === s.time;
        const disabled = !s.available;
        return (
          <button
            key={s.time}
            type="button"
            data-testid={`slot-${s.time}`}
            disabled={disabled}
            onClick={() => onChange(s.time)}
            className={`relative border px-2 py-2.5 text-sm font-mono tracking-wider transition-colors
              ${active ? "bg-white text-ink border-white" : "border-white/10 text-zinc-200 hover:border-white/40"}
              ${disabled ? "opacity-30 line-through cursor-not-allowed hover:border-white/10" : ""}`}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default Landing;